import { useEffect, useState } from 'react';

interface StudioLoadingProps {
  label?: string;
  /**
   * SET THIS WHENEVER THIS LOADER IS THE WHOLE SCREEN. It is not a styling
   * flag, it is the escape hatch: without it the 20s watchdog says "this is
   * taking longer than it should" and offers NO WAY OUT, which is a frozen
   * page with a caption.
   *
   * That is not hypothetical. On 2026-08-06 Kim was reported stuck spinning,
   * and nine page-level loaders (Teach, Calendar, Attendance Report,
   * Closures, Assistant, Comp Team, Workshops, Recital Builder, Family
   * Calendar) plus two route-level Suspense fallbacks were all missing it.
   * Teach is a teacher's home page, so hers was among them.
   *
   * Leave it OFF for a loader INSIDE a page that has other content, which is
   * what the restriction below exists for: several stalled section loaders
   * would otherwise stack duplicate full-width reload buttons on one screen.
   * Whole page, set it. Part of a page, do not.
   */
  fullPage?: boolean;
}

/**
 * How long a loading state may sit before it stops pretending everything is
 * fine. Bobbie stared at a stuck loading screen for twenty MINUTES on a
 * frozen tab; nothing in the app should ever spin silently that long again.
 * Twenty seconds is far beyond any healthy load and short enough that a
 * person is still standing there when the escape hatch appears.
 */
const WATCHDOG_MS = 20_000;

/**
 * Deliberately quiet loader (Bobbie, 2026-07-31: "We don't need the dancer
 * and the dots and I don't want it to load slower because of one"). One
 * small static line of text, no artwork, no animation loops - the loader
 * itself costs essentially nothing. The 20s watchdog with its Reload offer
 * stays exactly as before.
 */
export default function StudioLoading({ label, fullPage = false }: StudioLoadingProps) {
  const text = label ?? 'Loading...';
  const [tookTooLong, setTookTooLong] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setTookTooLong(true), WATCHDOG_MS);
    return () => window.clearTimeout(t);
  }, []);
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={label ?? 'Loading this page'}
      style={{
        minHeight: fullPage ? '70dvh' : undefined,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-6)',
        color: 'var(--ink-soft)',
      }}
    >
      <span style={{ fontSize: 14, fontWeight: 600, textAlign: 'center' }}>{text}</span>
      {tookTooLong && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--space-2)',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 14, color: 'var(--ink-soft)', lineHeight: 1.5 }}>
            This is taking longer than it should.
          </div>
          {/* The reload button only rides the full-page loader. Several
              stalled inline section loaders would otherwise stack duplicate
              full-width reload buttons on one page (review note); the message
              alone still tells the truth everywhere. */}
          {fullPage && (
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              font: 'inherit',
              fontSize: 15,
              fontWeight: 800,
              minHeight: 'var(--tap-target)',
              padding: '0 var(--space-5)',
              borderRadius: 'var(--radius-pill)',
              border: '1px solid var(--line)',
              background: 'var(--paper)',
              color: 'var(--ink)',
              boxShadow: 'var(--shadow-sm)',
              cursor: 'pointer',
            }}
          >
            Reload the page
          </button>
          )}
        </div>
      )}
    </div>
  );
}
